import { createSelector } from '@reduxjs/toolkit';

import { selectUsersById } from '@/slices/account/account.selector';
import { selectActiveEnterpriseId } from '@/slices/activeContext/activeContext.selector';
import enterpriseApi from '@/slices/enterprises/enterprise.api';
import type {
  Enterprise,
  HydratedEnterprise,
  OrganizationalUnit,
} from '@/slices/enterprises/enterprise.types';
import { createQueryEntitySelectors } from '@/slices/shared/queryEntitySelectors';
import type { RootState } from '@/store';

const flattenOrganizationalUnits = (
  organizationalUnits: OrganizationalUnit[] = [],
  seen = new Set<string>(),
): OrganizationalUnit[] =>
  organizationalUnits.flatMap((organizationalUnit) => {
    if (seen.has(organizationalUnit.id)) {
      return [];
    }

    seen.add(organizationalUnit.id);

    return [
      organizationalUnit,
      ...flattenOrganizationalUnits(
        organizationalUnit.subsidiaries ?? [],
        seen,
      ),
    ];
  });

const selectEnterprises = (state: RootState): Enterprise[] =>
  enterpriseApi.endpoints.getEnterprises.select()(state).data ?? [];

export const selectHydratedEnterprises = createSelector(
  [selectEnterprises, selectUsersById],
  (enterprises, usersById): HydratedEnterprise[] =>
    enterprises.map((enterprise) => ({
      ...enterprise,
      createdByResolved: enterprise.createdBy
        ? (usersById[enterprise.createdBy] ?? null)
        : null,
    })),
);

const hydratedEnterprisesEntitySelectors = createQueryEntitySelectors<
  RootState,
  HydratedEnterprise
>({
  selectList: selectHydratedEnterprises,
});

export const selectHydratedEnterprisesById =
  hydratedEnterprisesEntitySelectors.selectEntities;

export const selectEnterpriseById = (
  state: RootState,
  enterpriseId: string,
): Enterprise | undefined => {
  if (!enterpriseId) {
    return undefined;
  }

  return enterpriseApi.endpoints.getEnterprise.select({ enterpriseId })(state)
    .data;
};

export const getActiveEnterprise = createSelector(
  [
    (state: RootState) => state,
    (state: RootState) => selectActiveEnterpriseId(state),
  ],
  (state, activeEnterpriseId): Enterprise | undefined =>
    selectEnterpriseById(state, activeEnterpriseId),
);

export const selectFlattenedActiveEnterpriseOrganizationalUnits =
  createSelector([getActiveEnterprise], (enterprise): OrganizationalUnit[] =>
    flattenOrganizationalUnits(enterprise?.organizationalUnits ?? []),
  );

export const isActiveEnterpriseHasLicense = createSelector(
  [getActiveEnterprise],
  (enterprise): boolean => enterprise?.hasLicense ?? false,
);
